import { translate } from "@atoms/atom-client/i18n";
import type { FlightCaseDefinition } from "../products/FlightCaseDefinition";

export enum LTProducts {
  Koffer = "koffer",
  KeyboardCase = "keyboardcase",
  Haube = "haube",
  Truhe = "truhe",
  Case3d = "case3d",
  LCase = "lcase",
  SingleDoor = "singledoor",
  DoubleDoor = "doubledoor",
}
export type ProductConstructors = {
  [key in LTProducts]: typeof FlightCaseDefinition;
};

export type LTProductOption = {
  value: LTProducts | string;
  label: string;
};

/**
 * German product labels. These are stored in `PositionInfo.variant` and printed
 * in the PDF header, so they never follow the UI language: use
 * {@link getLTProductLabelDe} wherever a label is stored or printed.
 */
export const LT_PRODUCT_LABELS_DE: Readonly<Record<LTProducts, string>> = {
  [LTProducts.Koffer]: "Koffer",
  [LTProducts.KeyboardCase]: "Keyboard-Case",
  [LTProducts.Haube]: "Haube",
  [LTProducts.Truhe]: "Truhe",
  [LTProducts.Case3d]: "3D-Case",
  [LTProducts.LCase]: "L-Case",
  [LTProducts.SingleDoor]: "Single-Door-Case",
  [LTProducts.DoubleDoor]: "Double-Door-Case",
};

/** Product labels in the UI language (read on access, so never at import time). */
export const LTProductLabels: Readonly<Record<LTProducts, string>> = {
  get [LTProducts.Koffer]() {
    return translate(
      "app.manufacturer.products.koffer",
      LT_PRODUCT_LABELS_DE[LTProducts.Koffer],
    );
  },
  get [LTProducts.KeyboardCase]() {
    return translate(
      "app.manufacturer.products.keyboardCase",
      LT_PRODUCT_LABELS_DE[LTProducts.KeyboardCase],
    );
  },
  get [LTProducts.Haube]() {
    return translate(
      "app.manufacturer.products.haube",
      LT_PRODUCT_LABELS_DE[LTProducts.Haube],
    );
  },
  get [LTProducts.Truhe]() {
    return translate(
      "app.manufacturer.products.truhe",
      LT_PRODUCT_LABELS_DE[LTProducts.Truhe],
    );
  },
  get [LTProducts.Case3d]() {
    return translate(
      "app.manufacturer.products.case3d",
      LT_PRODUCT_LABELS_DE[LTProducts.Case3d],
    );
  },
  get [LTProducts.LCase]() {
    return translate(
      "app.manufacturer.products.lCase",
      LT_PRODUCT_LABELS_DE[LTProducts.LCase],
    );
  },
  get [LTProducts.SingleDoor]() {
    return translate(
      "app.manufacturer.products.singleDoor",
      LT_PRODUCT_LABELS_DE[LTProducts.SingleDoor],
    );
  },
  get [LTProducts.DoubleDoor]() {
    return translate(
      "app.manufacturer.products.doubleDoor",
      LT_PRODUCT_LABELS_DE[LTProducts.DoubleDoor],
    );
  },
};

export function getLTProductLabel(productType: string): string {
  const normalized = productType.trim();
  if (!normalized) return "";
  return LTProductLabels[normalized as LTProducts] ?? normalized;
}

/** German label for stored or printed values (position variant, PDF). */
export function getLTProductLabelDe(productType: string): string {
  const normalized = productType.trim();
  if (!normalized) return "";
  return LT_PRODUCT_LABELS_DE[normalized as LTProducts] ?? normalized;
}

export function getLTProductOptions(
  extraTypes: readonly unknown[] = [],
): LTProductOption[] {
  const byValue = new Map<string, string>(Object.entries(LTProductLabels));

  for (const rawType of extraTypes) {
    if (typeof rawType !== "string") continue;
    const type = rawType.trim();
    if (!type || byValue.has(type)) continue;
    byValue.set(type, type);
  }

  return Array.from(byValue.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
